import { inject, observer } from 'mobx-react';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { Instance } from 'mobx-state-tree';
import { TLCode } from '../../common/enums';
import { DutyExchangeItem } from '../../store/models/DutyExchangeItem';
import { Intent, Spinner } from '@blueprintjs/core';
import { saveAs } from 'file-saver';
import { renderGridDateTimeWithDetails, renderGridDateWithDay } from '../../store/utils';
import { SystemStatusStore } from '../../store';
import { SystemStatus } from '../../store/models/SystemStatus';
import {
  DataGrid,
  Column,
  SearchPanel,
  DataGridTypes,
  Export,
  FilterRow,
  HeaderFilter,
  Pager,
  Paging,
  Toolbar,
  Item,
  ColumnChooser,
  FilterPanel,
  Sorting,
} from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { Container } from '../common/styledComponents/StyledComponents';
import { Pharmacy } from '../../store/models';

interface PharmacyExchangeHistoryProps extends WithTranslateProps {
  dutyExchangeList: Array<Instance<typeof DutyExchangeItem>>;
  isLoading: boolean;
  exchangeHistoryIsLoading: boolean;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  secretariatId?: number;
  onSecretariatIdChanged: () => void;
  // pharmacyId: Array<Instance<typeof number>>;
  pharmacy?: Instance<typeof Pharmacy>;
  exchangeHistoryList?: any[];
}

interface PharmacyExchangeHistoryState {
  filter: string;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  year: number;
  checkBoxPastDateSelected?: boolean;
  pharmacyUser: boolean;
  exchangeItemHistory: any[];
  exchangeHistoryIsLoading: boolean;
  apbNumber?: number;
}

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 5px;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

@inject('SystemStatusStore')
@observer
class PharmacyExchangeHistory extends React.Component<PharmacyExchangeHistoryProps, PharmacyExchangeHistoryState> {
  constructor(props: PharmacyExchangeHistoryProps) {
    super(props);
    this.state = {
      filter: '',
      checkBoxPastDateSelected: false,
      year: new Date().getFullYear() - 1,
      pharmacyUser: false,
      exchangeItemHistory: [],
      exchangeHistoryIsLoading: false,
      apbNumber: undefined,
    };
  }

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
  }

  onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        if (!this.props.pharmacy) {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'PharmacyExchangeHistory.xlsx');
        } else {
          saveAs(
            new Blob([buffer], { type: 'application/octet-stream' }),
            'PharmacyExchangeHistory-' + this.props.pharmacy.apbNumber + '.xlsx',
          );
        }
      });
    });
  };

  render() {
    const { translate, exchangeHistoryList } = this.props;
    const { exchangeHistoryIsLoading } = this.state;

    return (
      <Container>
        {exchangeHistoryIsLoading ? (
          <SpinnerContainer>
            <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
          </SpinnerContainer>
        ) : (
          <>
            <DivLine />
            <DataGrid
              dataSource={exchangeHistoryList}
              showBorders={true}
              onExporting={this.onExporting}
              allowColumnReordering={true}
              rowAlternationEnabled={false}
              width="100%"
              allowColumnResizing={true}
              columnResizingMode={'widget'}
            >
              <Sorting mode="multiple" />
              <FilterRow visible={true} applyFilter={'auto'} />
              <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
              <HeaderFilter visible={true} />
              <FilterPanel visible={true} />
              <Export enabled={true} />
              <Paging defaultPageSize={20} />
              <Pager
                visible={true}
                allowedPageSizes={[5, 10, 20, 50, 100]}
                showPageSizeSelector={true}
                showInfo={true}
                showNavigationButtons={true}
              />
              <ColumnChooser enabled={true} />
              <Column
                dataType="date"
                format="dd/MM/yyyy"
                cellRender={renderGridDateWithDay}
                dataField="dutyDate"
                caption={translate(TLCode.EXCHANGE_HISTORY_DutyDate)}
                width={130}
                defaultSortOrder="asc"
                sortIndex={4}
              />
              <Column
                dataType="string"
                dataField="dutyType"
                caption={''}
                width={70}
                defaultSortOrder="asc"
                sortIndex={3}
              />
              <Column
                dataType="string"
                dataField="sender"
                caption={translate(TLCode.EXCHANGE_HISTORY_Sender)}
                minWidth={300}
                defaultSortOrder="asc"
                sortIndex={2}
              />
              <Column
                dataType="string"
                dataField="recipient"
                caption={translate(TLCode.EXCHANGE_HISTORY_Recipient)}
                width={300}
              />
              <Column
                dataType="datetime"
                format="dd/MM/yyyy HH:mm:ss"
                cellRender={renderGridDateTimeWithDetails}
                dataField="date"
                caption={translate(TLCode.EXCHANGE_HISTORY_Date)}
                minWidth={150}
                defaultSortOrder="desc"
                sortIndex={1}
              />
              <Column
                dataType="string"
                dataField="requestedBy"
                caption={translate(TLCode.EXCHANGE_HISTORY_REGISTERED_BY)}
                width={300}
              />
              <Column dataType="string" dataField="hasBeenDownloaded" visible={false} />
              <Toolbar>
                <Item location="before" name="searchPanel" />
                <Item location="after" name="exportButton" />
                <Item location="after" name="columnChooserButton" />
              </Toolbar>
            </DataGrid>
          </>
        )}
      </Container>
    );
  }
}

export default withTranslate(PharmacyExchangeHistory);
